import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Sparkles, Send, Loader2, Rocket, ExternalLink, CheckCircle2, Globe, ShoppingCart, FileText, Layout } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function stripJsonBlock(text) {
  // Remove ```json...``` blocks from display text, keep the rest
  return text.replace(/```json[\s\S]*?```/g, '').trim();
}

function PlanSummary({ spec }) {
  if (!spec) return null;
  const ctx = spec.business_context || {};
  const req = spec.requirements || {};
  const design = spec.design_brief || {};
  return (
    <div className="space-y-3 text-xs">
      <div>
        <span className="text-zinc-500 uppercase tracking-wider text-[10px]">Type</span>
        <p className="text-zinc-200 font-medium mt-0.5">{spec.website_type || 'website'}</p>
      </div>
      {ctx.industry && (
        <div>
          <span className="text-zinc-500 uppercase tracking-wider text-[10px]">Industry</span>
          <p className="text-zinc-200 font-medium mt-0.5">{ctx.industry}</p>
        </div>
      )}
      {ctx.target_audience && (
        <div>
          <span className="text-zinc-500 uppercase tracking-wider text-[10px]">Audience</span>
          <p className="text-zinc-300 mt-0.5">{ctx.target_audience}</p>
        </div>
      )}
      {req.pages?.length > 0 && (
        <div>
          <span className="text-zinc-500 uppercase tracking-wider text-[10px]">Pages</span>
          <div className="flex flex-wrap gap-1 mt-1">
            {req.pages.map((p, i) => (
              <span key={i} className="px-2 py-0.5 bg-zinc-800 rounded text-zinc-300 capitalize">{p}</span>
            ))}
          </div>
        </div>
      )}
      {req.features?.length > 0 && (
        <div>
          <span className="text-zinc-500 uppercase tracking-wider text-[10px]">Features</span>
          <div className="flex flex-wrap gap-1 mt-1">
            {req.features.map((f, i) => (
              <span key={i} className="px-2 py-0.5 bg-emerald-500/15 border border-emerald-500/30 rounded text-emerald-300 text-[11px]">{f.replace(/_/g, ' ')}</span>
            ))}
          </div>
        </div>
      )}
      {design.style && (
        <div>
          <span className="text-zinc-500 uppercase tracking-wider text-[10px]">Style</span>
          <p className="text-zinc-300 mt-0.5 capitalize">{design.style}</p>
        </div>
      )}
      {design.colors?.length > 0 && (
        <div>
          <span className="text-zinc-500 uppercase tracking-wider text-[10px]">Colors</span>
          <div className="flex gap-1.5 mt-1">
            {design.colors.map((c, i) => (
              <div key={i} className="w-6 h-6 rounded-md border border-zinc-700" style={{ background: c }} title={c} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const QUICK_PROMPTS = [
  { icon: ShoppingCart, label: 'Online Store', prompt: 'Create an online store website called "ShopEase". Business: multi-category e-commerce store selling fashion, electronics, and home goods. Audience: urban professionals aged 25-45. Pages: home, shop, product detail, cart, about, contact. Features: product_catalog, shopping_cart, checkout, user_accounts, wishlist, featured_products. Design: clean modern style with teal (#0d9488) as primary color. Responsive, SEO optimized. Generate the full JSON plan now, do not ask any questions.' },
  { icon: Globe, label: 'Professional Services', prompt: 'Create a professional services website called "NexusAdvisors". Business: financial advisory and wealth management firm. Audience: high-net-worth individuals and growing businesses. Pages: home, services, team, case-studies, contact. Features: contact_form, testimonials, service_cards, newsletter_signup, client_portal_link. Design: sophisticated style with dark navy (#0f172a) and gold (#d4a853) accents. Responsive, SEO optimized. Generate the full JSON plan now, do not ask any questions.' },
  { icon: FileText, label: 'Portfolio & Blog', prompt: 'Create a portfolio website called "CreativeEdge". Business: digital marketing agency showcasing client work. Audience: startups and SMBs looking for marketing partners. Pages: home, work, services, blog, about, contact. Features: project_gallery, case_studies, blog_posts, contact_form, client_logos. Design: bold creative style with coral (#f43f5e) and charcoal (#1e293b). Generate the full JSON plan now, do not ask any questions.' },
  { icon: Layout, label: 'Product Launch', prompt: 'Create a product launch landing page called "VeloApp". Business: fitness tracking mobile app launching soon. Audience: health-conscious millennials and Gen-Z. Pages: home. Features: hero_section, features_grid, app_screenshots, testimonials, download_cta, faq, early_access_form. Design: energetic style with lime green (#84cc16) primary on dark background. Generate the full JSON plan now, do not ask any questions.' },
];

const AIWebAgent = () => {
  const navigate = useNavigate();
  const [projectId, setProjectId] = useState(null);
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content:
        "Welcome to Site Planner! Tell me about your business and the website you need. I'll create a detailed blueprint, then build it for you. Choose a template below or describe your project in your own words.",
    },
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [building, setBuilding] = useState(false);
  const [projectSpec, setProjectSpec] = useState(null);
  const [builtWebsiteId, setBuiltWebsiteId] = useState(null);
  const chatEndRef = useRef(null);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  useEffect(() => {
    const initSession = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}/api/ai/web-agent/session`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error('Failed to start session');
        const data = await res.json();
        setProjectId(data.project_id);
      } catch (err) {
        console.error(err);
        toast.error('Could not start AI Web Agent. Please try again.');
      }
    };
    initSession();
  }, []);

  const sendMessage = async (overrideInput) => {
    const text = overrideInput || input;
    if (!text.trim() || !projectId) return;

    const newMessages = [...messages, { role: 'user', content: text }];
    setMessages(newMessages);
    setInput('');
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/ai/web-agent/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ project_id: projectId, messages: newMessages }),
      });

      let data;
      try { data = await res.clone().json(); } catch { data = null; }
      if (!res.ok) throw new Error((data && data.detail) || 'AI Web Agent error');
      if (!data || !data.reply) throw new Error('No reply from AI agent. Please try again.');

      const displayReply = stripJsonBlock(data.reply) || 'Plan generated! You can now build your website.';
      setMessages((prev) => [...prev, { role: 'assistant', content: displayReply }]);

      if (data.project_spec) {
        setProjectSpec(data.project_spec);
        toast.success('Website plan ready! Click "Build My Website" to create it.');
      }
    } catch (err) {
      console.error(err);
      toast.error(err.message || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleBuild = async () => {
    if (!projectId || !projectSpec) {
      toast.error('Plan is not ready yet. Continue the conversation first.');
      return;
    }
    setBuilding(true);
    try {
      const token = localStorage.getItem('token');
      // Start the build (async)
      const res = await fetch(`${API_URL}/api/ai/web-agent/build`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ project_id: projectId }),
      });
      let data;
      try { data = await res.clone().json(); } catch { data = null; }
      if (!res.ok) throw new Error((data && data.detail) || 'Failed to start build');

      toast.info('Building your website... This may take a minute.');

      // Poll for completion
      const pollInterval = setInterval(async () => {
        try {
          const statusRes = await fetch(`${API_URL}/api/ai/web-agent/project/${projectId}/status`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          const statusData = await statusRes.json();

          if (statusData.status === 'completed' && statusData.website_id) {
            clearInterval(pollInterval);
            setBuiltWebsiteId(statusData.website_id);
            setBuilding(false);
            toast.success('Website created successfully!');
            setMessages((prev) => [...prev, {
              role: 'assistant',
              content: 'Your website has been built! Click "Open in Editor" to customize it.',
            }]);
          } else if (statusData.status === 'failed') {
            clearInterval(pollInterval);
            setBuilding(false);
            toast.error(statusData.error || 'Build failed. Please try again.');
          }
        } catch {
          // ignore polling errors, keep trying
        }
      }, 3000);

      // Safety timeout: stop polling after 3 minutes
      setTimeout(() => {
        clearInterval(pollInterval);
        if (!builtWebsiteId) {
          setBuilding(false);
          toast.error('Build timed out. Please check your dashboard for the website.');
        }
      }, 180000);

    } catch (err) {
      console.error(err);
      setBuilding(false);
      toast.error(err.message || 'Something went wrong while building the site');
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-white" data-testid="ai-web-agent-page">
      {/* Header */}
      <header className="border-b border-zinc-800 px-6 py-3 sticky top-0 z-40 bg-zinc-950/90 backdrop-blur-md">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')} className="text-zinc-400 hover:text-white" data-testid="back-to-dashboard">
              <ArrowLeft className="w-4 h-4 mr-2" />Back to Dashboard
            </Button>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
                <Sparkles className="w-4 h-4" />
              </div>
              <div>
                <h1 className="text-base font-semibold">Site Planner</h1>
                <p className="text-[10px] text-zinc-500">Plan and generate your website with AI</p>
              </div>
            </div>
          </div>
          {builtWebsiteId && (
            <Button size="sm" onClick={() => navigate(`/edit/${builtWebsiteId}`)} className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg" data-testid="open-editor-btn">
              <ExternalLink className="w-4 h-4 mr-2" />Open in Editor
            </Button>
          )}
        </div>
      </header>

      <main className="max-w-6xl mx-auto p-4 grid grid-cols-1 lg:grid-cols-[1fr,340px] gap-4" style={{ height: 'calc(100vh - 56px)' }}>
        {/* Chat panel */}
        <section className="bg-zinc-900/50 border border-zinc-800 rounded-xl flex flex-col overflow-hidden" data-testid="chat-panel">
          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {messages.map((m, idx) => (
              <div key={idx} className={`max-w-[80%] text-sm rounded-2xl px-4 py-3 leading-relaxed ${
                m.role === 'assistant'
                  ? 'bg-zinc-800/70 text-zinc-100 border border-zinc-700/50'
                  : 'ml-auto bg-emerald-600 text-white'
              }`} style={{ whiteSpace: 'pre-wrap' }} data-testid={`chat-message-${idx}`}>
                {m.content}
              </div>
            ))}
            {loading && (
              <div className="flex items-center gap-2 text-xs text-zinc-400 py-2">
                <Loader2 className="w-3.5 h-3.5 animate-spin" /> Planning...
              </div>
            )}
            <div ref={chatEndRef} />
          </div>

          {/* Quick prompts (only shown when no messages sent yet) */}
          {messages.length <= 1 && !loading && (
            <div className="px-4 pb-2">
              <p className="text-[10px] text-zinc-500 mb-2 uppercase tracking-wider">Quick Start</p>
              <div className="grid grid-cols-2 gap-2">
                {QUICK_PROMPTS.map((qp) => (
                  <button key={qp.label} onClick={() => sendMessage(qp.prompt)}
                    className="flex items-center gap-2 px-3 py-2.5 bg-zinc-800/60 border border-zinc-700/50 rounded-lg text-left text-xs text-zinc-300 hover:bg-zinc-700/60 hover:border-zinc-600 transition-colors"
                    data-testid={`quick-prompt-${qp.label.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <qp.icon className="w-4 h-4 text-emerald-400 shrink-0" />
                    {qp.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Input */}
          <div className="border-t border-zinc-800 p-3">
            <Textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              rows={2}
              placeholder="Tell me about your business and what you need... (e.g. 'financial advisory firm with client portal and booking')"
              className="bg-zinc-800/50 border-zinc-700 focus:border-emerald-500 resize-none text-sm"
              data-testid="chat-input"
            />
            <div className="flex justify-end mt-2">
              <Button onClick={() => sendMessage()} disabled={loading || !input.trim()} className="rounded-lg px-4 h-8 text-xs bg-emerald-600 hover:bg-emerald-500" data-testid="send-btn">
                {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                <span className="ml-1.5">Send</span>
              </Button>
            </div>
          </div>
        </section>

        {/* Plan + Build panel */}
        <aside className="bg-zinc-900/50 border border-zinc-800 rounded-xl flex flex-col overflow-hidden" data-testid="plan-panel">
          <div className="p-3 border-b border-zinc-800">
            <h2 className="text-sm font-semibold flex items-center gap-2">
              {projectSpec ? <CheckCircle2 className="w-4 h-4 text-emerald-400" /> : <Layout className="w-4 h-4 text-zinc-500" />}
              {projectSpec ? 'Plan Ready' : 'Website Plan'}
            </h2>
            <p className="text-[10px] text-zinc-500 mt-0.5">
              {projectSpec ? 'Review your blueprint and launch the build' : 'Describe your project to generate a blueprint'}
            </p>
          </div>

          <div className="flex-1 overflow-y-auto p-3">
            {projectSpec ? (
              <PlanSummary spec={projectSpec} />
            ) : (
              <div className="flex flex-col items-center justify-center h-full text-center py-8">
                <div className="w-12 h-12 rounded-xl bg-zinc-800 flex items-center justify-center mb-3">
                  <Sparkles className="w-5 h-5 text-zinc-500" />
                </div>
                <p className="text-xs text-zinc-500 max-w-[200px]">
                  Share your business details and the AI will generate a complete site blueprint here.
                </p>
              </div>
            )}
          </div>

          {/* Build actions */}
          <div className="p-3 border-t border-zinc-800 space-y-2">
            {builtWebsiteId ? (
              <>
                <Button onClick={() => navigate(`/edit/${builtWebsiteId}`)} className="w-full bg-emerald-500 hover:bg-emerald-600 h-10 text-sm font-semibold" data-testid="edit-website-btn">
                  <ExternalLink className="w-4 h-4 mr-2" />Open in Editor
                </Button>
                <Button variant="outline" onClick={() => { setBuiltWebsiteId(null); setProjectSpec(null); setMessages([{ role: 'assistant', content: "Ready for another project! Describe your next website and I'll plan it out." }]); }}
                  className="w-full border-zinc-700 text-zinc-300 h-9 text-xs" data-testid="build-another-btn">
                  Build Another Website
                </Button>
              </>
            ) : (
              <Button onClick={handleBuild} disabled={!projectSpec || building || loading}
                className="w-full bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 h-11 text-sm font-semibold disabled:opacity-40"
                data-testid="build-website-btn"
              >
                {building ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Generating Site...</>
                ) : (
                  <><Rocket className="w-4 h-4 mr-2" />Generate Website</>
                )}
              </Button>
            )}
          </div>
        </aside>
      </main>
    </div>
  );
};

export default AIWebAgent;
